import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const MIN_PANEL_HEIGHT = 140;
const MAX_PANEL_HEIGHT = 320;
const PANEL_OFFSET = 8;
const VIEWPORT_PADDING = 12;
const CLIPPING_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'hidden', 'clip']);

function isClippingElement(element) {
  if (!element || typeof window === 'undefined') return false;
  const styles = window.getComputedStyle(element);
  return [styles.overflow, styles.overflowX, styles.overflowY].some(value => CLIPPING_OVERFLOW_VALUES.has(value));
}

function intersectRect(baseRect, nextRect) {
  const left = Math.max(baseRect.left, nextRect.left);
  const top = Math.max(baseRect.top, nextRect.top);
  const right = Math.min(baseRect.right, nextRect.right);
  const bottom = Math.min(baseRect.bottom, nextRect.bottom);

  return {
    left,
    top,
    right,
    bottom,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

function getViewportRect() {
  return {
    left: VIEWPORT_PADDING,
    top: VIEWPORT_PADDING,
    right: window.innerWidth - VIEWPORT_PADDING,
    bottom: window.innerHeight - VIEWPORT_PADDING,
    width: Math.max(0, window.innerWidth - VIEWPORT_PADDING * 2),
    height: Math.max(0, window.innerHeight - VIEWPORT_PADDING * 2),
  };
}

function getBoundaryRect(element) {
  let rect = getViewportRect();
  let current = element?.parentElement || null;

  while (current && current !== document.body) {
    if (isClippingElement(current)) {
      rect = intersectRect(rect, current.getBoundingClientRect());
    }
    current = current.parentElement;
  }

  return rect;
}

export function useDropdownPosition(referenceRef, open) {
  const dropdownRef = useRef(null);
  const [placement, setPlacement] = useState('bottom');
  const [panelStyle, setPanelStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const updatePosition = () => {
      const reference = referenceRef.current;
      if (!reference) return;

      const triggerRect = reference.getBoundingClientRect();
      const boundaryRect = getBoundaryRect(reference);
      const availableBelow = Math.max(0, boundaryRect.bottom - triggerRect.bottom - PANEL_OFFSET);
      const availableAbove = Math.max(0, triggerRect.top - boundaryRect.top - PANEL_OFFSET);
      const shouldOpenUp = availableBelow < MIN_PANEL_HEIGHT && availableAbove > availableBelow;
      const availableHeight = shouldOpenUp ? availableAbove : availableBelow;
      const resolvedHeight = Math.max(
        Math.min(MAX_PANEL_HEIGHT, availableHeight || MAX_PANEL_HEIGHT),
        Math.min(MIN_PANEL_HEIGHT, Math.max(availableBelow, availableAbove, 0))
      );

      const width = Math.min(triggerRect.width, boundaryRect.width || triggerRect.width);
      const maxLeft = Math.max(boundaryRect.left, boundaryRect.right - width);
      const left = Math.min(Math.max(triggerRect.left, boundaryRect.left), maxLeft);
      const top = shouldOpenUp
        ? Math.max(boundaryRect.top, triggerRect.top - PANEL_OFFSET - resolvedHeight)
        : Math.min(triggerRect.bottom + PANEL_OFFSET, boundaryRect.bottom - resolvedHeight);

      setPlacement(shouldOpenUp ? 'top' : 'bottom');
      setPanelStyle({
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(width)}px`,
        maxHeight: `${Math.round(resolvedHeight)}px`,
        zIndex: 1200,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, referenceRef]);

  useEffect(() => {
    if (!open) {
      setPlacement('bottom');
      setPanelStyle(null);
    }
  }, [open]);

  return { dropdownRef, panelStyle, placement };
}
